import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { withRouter } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';

import { withAccount } from '../../../components/AccountContext';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlShape, locationShape } from '../../../components/prop-types';
import AccountInvitationsTable from './AccountInvitationsTable';
import { parse, filterPaginationWithSize, emptyConnection } from '../../../utils/UrlUtil';

const QUERY_INVITATIONS = gql`
query listClientAccountInvitations($partnerId: ID!,
  $first: Int,
  $last: Int,
  $after: String,
  $before: String,
  $filter: ClientInvitationFilter,
  $sort: ClientInvitationSortSpecifier) {
  account(id: $partnerId) {
    id
    invitations: pendingClientInvitations(first: $first,
      last: $last,
      before: $before,
      after: $after,
      filter: $filter,
      sort: $sort
    ) {
      edges {
        node {
          id
          invitedBy
          invitedAt
          expiresAt
          email
          fullName
          type
        }
        cursor
      }
      pageInfo {
        startCursor
        endCursor
        hasPreviousPage
        hasNextPage
      }
      totalCount
      indexFrom
      indexTo
    }
  }
}
`;

/**
 * Cancel a client account invitation.

{
  "input": {
    "invitationId": 123
  }
}
 */
const CANCEL_INVITATION = gql`
  mutation cancelClientAccountInvitation($input: CancelClientInvitationInput!) {
    cancelClientInvitation(input: $input) {
      clientMutationId
    }
  }
`;

/**
 * Send the email invitation for the new client account.
 {
  "input": {
    "invitationId": 123
  }
 }
 */
const SEND_INVITATION = gql`
  mutation sendClientAccountInvitation($input: SendClientInvitationInput!) {
    sendClientInvitation(input: $input) {
      clientMutationId
      clientInvitation {
        id
        invitedAt
        expiresAt
        email
        fullName
      }
    }
  }
`;

function AccountInvitationsContainer(props) {
  const { children, variables } = props;
  const { loading, data, error } = useQuery(QUERY_INVITATIONS, {
    variables,
    fetchPolicy: 'network-only',
  });

  const [sendInvitationMutation, sendInvitationResult] = useMutation(SEND_INVITATION);

  const [cancelInvitationMutation, cancelInvitationResult] = useMutation(CANCEL_INVITATION, {
    refetchQueries: ['listClientAccountInvitations'],
  });

  return children({
    invitations: { loading, data, error },
    sendInvitation: { mutation: sendInvitationMutation, result: sendInvitationResult },
    cancelInvitation: { mutation: cancelInvitationMutation, result: cancelInvitationResult },
  });
}

function AccountInvitations(props) {
  const { location, growl, currentAccount } = props;

  const params = parse(location.search);
  const pagination = filterPaginationWithSize(params);

  const variables = {
    ...pagination,
    partnerId: currentAccount,
    sort: params.sort,
    filter: params.filter,
  };
  return (
    <AccountInvitationsContainer variables={variables}>
      {({ invitations: { loading, data, error }, sendInvitation, cancelInvitation }) => {
        if (error) {
          growl.show({
            severity: 'error', sticky: true, detail: error.message,
          });
        }

        const connection = data && data.account ? data.account.invitations : emptyConnection();

        return (
          <AccountInvitationsTable
            connection={connection}
            loading={loading}
            sendInvitation={sendInvitation}
            cancelInvitation={cancelInvitation}
          />
        );
      }}
    </AccountInvitationsContainer>
  );
}

AccountInvitations.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
  location: locationShape.isRequired,
};

export default withRouter(withAccount(withGrowl(AccountInvitations)));
